/**
 * Dev-only builder/user override for AI search — graft/A5.
 *
 * WHAT: lets a dev/QA session search AS a different builder/user without
 * re-logging, by appending builder_id/user_id query params to search
 * service requests. The search service honors those params ONLY in its
 * AUTH_DISABLED dev posture (app/api/auth.py in BTNet-Search): in every
 * deployed environment the JWT claim wins and the params are ignored, so
 * this cannot cross tenants anywhere that matters.
 *
 * HOW IT'S GATED CLIENT-SIDE (defense in depth, both must hold):
 *   1. environmentInfo.isProduction === false, AND
 *   2. the search service actually exposes its dev surface — probed once
 *      via GET {searchBaseUrl}dev/users (404/error => switcher hidden).
 * The probe is the load-bearing gate: only an AUTH_DISABLED search
 * service answers it, so the switcher renders exactly where the override
 * works and nowhere else.
 */

const STORAGE_KEY = "bt:aiSearchDevContext";

// The override is only MEANINGFUL where the dev surface exists. The probe
// result is recorded here (module state, per-page-load) and loadDevContext
// returns {} until a probe has CONFIRMED the surface this session — so a
// persisted override from an earlier local session can never retarget
// searches in a posture where the switcher (the only UI that can clear it)
// doesn't even render (reviews 1529290/1529291). sessionStorage-free by
// design: module state resets on every full page load, forcing a re-probe.
let devSurfaceConfirmed = false;

/** Record the dev-surface probe result; clears a stale persisted override
 * outright when the surface is gone (the switcher can't, it isn't mounted). */
export function setDevSurfaceConfirmed(confirmed: boolean): void {
    devSurfaceConfirmed = confirmed;
    if (!confirmed) {
        try {
            window.localStorage.removeItem(STORAGE_KEY);
        } catch {
            // best effort
        }
    }
}

export interface DevContext {
    builderId?: string;
    userId?: string;
}

export function loadDevContext(): DevContext {
    if (!devSurfaceConfirmed) return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as DevContext;
        return {
            builderId: typeof parsed.builderId === "string" ? parsed.builderId : undefined,
            userId: typeof parsed.userId === "string" ? parsed.userId : undefined,
        };
    } catch {
        return {};
    }
}

export function saveDevContext(ctx: DevContext): void {
    try {
        if (!ctx.builderId && !ctx.userId) {
            window.localStorage.removeItem(STORAGE_KEY);
        } else {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
        }
    } catch {
        // best effort
    }
}

/** Query-string suffix for search-service calls ("" when no override). */
export function devContextParams(ctx: DevContext): string {
    const p = new URLSearchParams();
    if (ctx.builderId) p.set("builder_id", ctx.builderId);
    if (ctx.userId) p.set("user_id", ctx.userId);
    const s = p.toString();
    return s ? `?${s}` : "";
}

/** Probe the search service's dev surface. Resolves to the tenant/user
 * lists when available, null in any deployed environment. */
export async function probeDevSurface(searchBaseUrl: string): Promise<{
    tenants: Array<{ builderId: string; servedBy: string; entities: number }>;
    users: Array<{ id: string; name: string; role: string }>;
} | null> {
    try {
        const tRes = await fetch(`${searchBaseUrl}dev/tenants`);
        if (!tRes.ok) {
            setDevSurfaceConfirmed(false);
            return null;
        }
        const tenants = (await tRes.json()).tenants ?? [];
        const uRes = await fetch(`${searchBaseUrl}dev/users`);
        const users = uRes.ok ? ((await uRes.json()).users ?? []) : [];
        setDevSurfaceConfirmed(true);
        return { tenants, users };
    } catch {
        setDevSurfaceConfirmed(false);
        return null;
    }
}

/** Fetch the user roster for a specific builder (dev surface only). */
export async function fetchDevUsers(
    searchBaseUrl: string,
    builderId?: string,
): Promise<Array<{ id: string; name: string; role: string }>> {
    try {
        const res = await fetch(
            `${searchBaseUrl}dev/users${builderId ? `?builder_id=${builderId}` : ""}`,
        );
        if (!res.ok) return [];
        return (await res.json()).users ?? [];
    } catch {
        return [];
    }
}
